import { Nullable } from '@rbi-ctg/frontend';
import { IPrices, IVendorConfig } from '@rbi-ctg/menu';
import { IRestaurant } from '@rbi-ctg/store';
import { ISystemwideOffersFragment, ISystemwideTierGroupFragment } from 'generated/sanity-graphql';
import { ServiceMode } from 'state/service-mode';
import { isDelivery } from 'utils/service-mode';
import { PluTypes, concatenateSizePlu } from 'utils/vendor-config';

import {
  OfferWithPrice,
  SystemwideOffer,
  ValidSystemwideTierGroup,
  buildTierGroupOfferLookup,
  getPriceTargets,
  selectOffer,
  shouldExcludeNonSelectedTierGroupOffer,
  validateTierGroup,
} from './base';

function getVendor(store: IRestaurant, serviceMode: Nullable<ServiceMode>): string {
  const posVendor = (store.pos?.vendor ?? '').trim().toLowerCase();
  return isDelivery(serviceMode) ? `${posVendor}Delivery` : posVendor;
}

function getMenuItemsPrice(
  { menu_items }: ValidSystemwideTierGroup,
  vendor: string,
  prices: IPrices
): number {
  if (!menu_items || !menu_items.length) {
    return 0;
  }

  let totalPrice = 0;
  for (const menuItem of menu_items) {
    const vendorConfig = menuItem?.vendorConfigs?.[vendor] as IVendorConfig | undefined;
    if (!vendorConfig) {
      continue;
    }

    const price = mapVendorConfigToPrice({ vendorConfig, prices });
    if (typeof price === 'number' && !isNaN(price)) {
      totalPrice += price;
    }
  }

  return totalPrice;
}

type MapVendorConfigToPriceInput = {
  vendorConfig: IVendorConfig;
  prices: IPrices;
};

function mapVendorConfigToPrice({ vendorConfig, prices }: MapVendorConfigToPriceInput) {
  switch (vendorConfig.pluType) {
    case PluTypes.CONSTANT:
      return prices?.[vendorConfig.constantPlu];
    case PluTypes.IGNORE:
      return 0;
    case PluTypes.PARENT_CHILD:
      const { plu: parentPlu, childPlu } = vendorConfig.parentChildPlu;
      const parentChildPlu = `${parentPlu}-${childPlu}`;

      return prices && prices?.[parentPlu] + prices?.[childPlu] + prices?.[parentChildPlu];
    case PluTypes.SIZE_BASED:
      const sizeBasedPlu = concatenateSizePlu(vendorConfig.sizeBasedPlu) ?? '';
      return prices?.[sizeBasedPlu];
    default:
      return 0;
  }
}

/**
 * Processes a single tier group to select the best offer based on business rules.
 *
 * Validates the tier group, calculates menu item pricing, and selects the offer
 * whose price is closest to the ideal discount target. If multiple offers are
 * equally close, chooses the highest-priced one to maximize revenue.
 *
 * @returns The selected offer with `isTieredOffer` flag set, or null if no suitable offer found
 */
export function processTierGroup_Legacy(
  tierGroup: ISystemwideTierGroupFragment,
  vendor: string,
  prices: IPrices
): SystemwideOffer | null {
  const [validatedTierGroup] = validateTierGroup(tierGroup);
  if (!validatedTierGroup) {
    return null;
  }

  const itemsPrice = getMenuItemsPrice(validatedTierGroup, vendor, prices);
  const priceTargets = getPriceTargets(validatedTierGroup, itemsPrice);

  const offersWithPrice: OfferWithPrice[] = validatedTierGroup.offers
    .map(offer => {
      if (!offer.offerPrice) {
        return null;
      }
      return { offer, price: offer.offerPrice };
    })
    .filter((offer): offer is OfferWithPrice => offer !== null);

  const [selectedOffer] = selectOffer(priceTargets, offersWithPrice);

  return selectedOffer;
}

export type TransformTierGroupsToOffersOptions_Legacy = {
  items: (ISystemwideOffersFragment | ISystemwideTierGroupFragment)[];
  store: IRestaurant;
  serviceMode: ServiceMode;
  prices: IPrices;
};

/**
 * Transforms a mixed list of offers and tier groups into a flat list of valid offers.
 *
 * Regular offers pass through unchanged. Tier groups are processed to select their
 * best offer based on current pricing and business rules. Invalid tier groups or
 * those with no suitable offers are filtered out.
 *
 * @returns Flattened array containing only valid offers, maintaining original order where possible
 */
export function transformTierGroupsToOffers_Legacy({
  items,
  store,
  serviceMode,
  prices,
}: TransformTierGroupsToOffersOptions_Legacy): ISystemwideOffersFragment[] {
  if (!items.length) {
    return [];
  }

  const vendor: string = getVendor(store, serviceMode);
  const legacyOfferIdsToRemove = new Set<string>();
  const tierGroupOfferLookup = buildTierGroupOfferLookup(items);

  const transformed = items
    .map(item => {
      if (item.__typename !== 'SystemwideTierGroup') {
        return item; // Pass through regular offers
      }

      const selectedOffer = processTierGroup_Legacy(item, vendor, prices);

      // Collect legacy offer IDs to remove
      if (item.legacyOffers?.length) {
        item.legacyOffers.forEach(legacyOffer => {
          if (legacyOffer?._id) {
            legacyOfferIdsToRemove.add(legacyOffer._id);
          }
        });
      }

      return selectedOffer;
    })
    .filter(offer => {
      if (!offer) {
        // Filter out tier groups that yielded no valid offer
        return false;
      }

      if (legacyOfferIdsToRemove.has(offer._id)) {
        // Remove legacy offers that were replaced by tier group selections
        return false;
      }
      // @ts-ignore
      if (shouldExcludeNonSelectedTierGroupOffer(offer, tierGroupOfferLookup)) {
        // Remove non-selected tier group offers
        return false;
      }

      return true;
    });
  // @ts-ignore
  return transformed;
}
